import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTenantInfo, updateTenantInfo } from '../services/tenantService';
import { Field, FormSection, Notice } from './patterns';

const MIN_DAYS = 1;
const MAX_DAYS = 30;

const SpraySettings: React.FC = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [longRunningDays, setLongRunningDays] = useState<number>(5);
  const [savedLongRunningDays, setSavedLongRunningDays] = useState<number>(5);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageKind, setMessageKind] = useState<'success' | 'danger'>('success');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [seasonStartDate,      setSeasonStartDate]      = useState<string>('');
  const [savedSeasonStartDate, setSavedSeasonStartDate] = useState<string>('');

  useEffect(() => {
    getTenantInfo()
      .then(tenant => {
        setEnabled(!!(tenant?.applicationSettings?.allowBackCaptureBeyond90Days ?? tenant?.spraySettings?.allowBackCaptureBeyond90Days));
        const v = (tenant?.applicationSettings?.longRunningSessionDays ?? tenant?.spraySettings?.longRunningSessionDays) ?? 5;
        setLongRunningDays(v);
        setSavedLongRunningDays(v);
        const sd = tenant?.applicationSettings?.seasonStartDate ?? tenant?.spraySettings?.seasonStartDate;
        const sdStr = sd ? new Date(sd).toISOString().slice(0, 10) : '';
        setSeasonStartDate(sdStr);
        setSavedSeasonStartDate(sdStr);
      })
      .catch(() => { /* silent — defaults stand */ });
  }, []);

  const handleToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked;
    setEnabled(next);
    setSaving(true);
    setMessage(null);
    try {
      await updateTenantInfo({ applicationSettings: { allowBackCaptureBeyond90Days: next } });
      setMessage(t('settings.spray.saveSuccess'));
      setMessageKind('success');
    } catch {
      setEnabled(!next); // revert UI
      setMessage(t('settings.spray.saveFailure'));
      setMessageKind('danger');
    } finally {
      setSaving(false);
    }
  };

  const handleLongRunningChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLongRunningDays(Number(e.target.value));
    if (validationError) setValidationError(null);
  };

  const handleLongRunningBlur = async () => {
    if (longRunningDays === savedLongRunningDays) return;
    if (!Number.isFinite(longRunningDays) || longRunningDays < MIN_DAYS || longRunningDays > MAX_DAYS) {
      setValidationError(t('settings.spray.longRunningSessionDays.outOfRange'));
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await updateTenantInfo({ applicationSettings: { longRunningSessionDays: longRunningDays } });
      setSavedLongRunningDays(longRunningDays);
      setMessage(t('settings.spray.saveSuccess'));
      setMessageKind('success');
    } catch {
      setLongRunningDays(savedLongRunningDays);
      setMessage(t('settings.spray.saveFailure'));
      setMessageKind('danger');
    } finally {
      setSaving(false);
    }
  };

  const handleSeasonStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSeasonStartDate(e.target.value);
  };

  const handleSeasonStartBlur = async () => {
    if (seasonStartDate === savedSeasonStartDate) return;
    setSaving(true);
    setMessage(null);
    const payload = seasonStartDate === '' ? null : seasonStartDate;
    try {
      await updateTenantInfo({ applicationSettings: { seasonStartDate: payload } });
      setSavedSeasonStartDate(seasonStartDate);
      setMessage(t('settings.spray.saveSuccess'));
      setMessageKind('success');
    } catch {
      setSeasonStartDate(savedSeasonStartDate);
      setMessage(t('settings.spray.saveFailure'));
      setMessageKind('danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-general">
      <FormSection
        id="spray-back-capture-override"
        title={t('settings.spray.overrideTitle')}
        description={t('settings.spray.overrideDescription')}
      >
        <Field
          label={t('settings.spray.overrideToggle')}
          controlId="spray-back-capture-override-toggle"
        >
          <input
            id="spray-back-capture-override-toggle"
            type="checkbox"
            checked={enabled}
            disabled={saving}
            onChange={handleToggle}
          />
        </Field>
      </FormSection>

      <FormSection
        id="spray-dashboard-thresholds"
        title={t('settings.spray.dashboardThresholds.title')}
        description={t('settings.spray.dashboardThresholds.description')}
      >
        <Field
          label={t('settings.spray.longRunningSessionDays.label')}
          controlId="spray-longRunningSessionDays"
          helpText={t('settings.spray.longRunningSessionDays.helpText')}
          errorText={validationError ?? undefined}
        >
          <input
            id="spray-longRunningSessionDays"
            className="pat-input"
            type="number"
            min={MIN_DAYS}
            max={MAX_DAYS}
            value={longRunningDays}
            disabled={saving}
            onChange={handleLongRunningChange}
            onBlur={handleLongRunningBlur}
          />
        </Field>
        <Field
          label={t('settings.spray.seasonStartDate.label')}
          controlId="spray-seasonStartDate"
          helpText={t('settings.spray.seasonStartDate.helpText')}
        >
          <input
            id="spray-seasonStartDate"
            className="pat-input"
            type="date"
            value={seasonStartDate}
            disabled={saving}
            onChange={handleSeasonStartChange}
            onBlur={handleSeasonStartBlur}
          />
        </Field>
      </FormSection>

      {message && (
        <Notice tone={messageKind} role="status">
          {message}
        </Notice>
      )}
    </div>
  );
};

export default SpraySettings;
